import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);

    // BSG FIX: marquee getting stuck after first tap on mobile.
    // `pointerenter` fires on touch as soon as the finger lands, slowing the
    // marquee down — but touch devices never fire a matching `pointerleave`
    // (there's no hover state to leave), so the animation stayed slowed
    // forever after the first touch. We only wire up the hover-driven
    // slow-down/speed-up for real hover-capable pointers (mouse/pen-hover),
    // and explicitly resume full speed on touchend/pointerup/pointercancel
    // as a safety net for any touch interaction that still reaches us.
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
    this.addEventListener('pointerup', this.#resumeIfTouch);
    this.addEventListener('pointercancel', this.#resumeIfTouch);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('pointerup', this.#resumeIfTouch);
    this.removeEventListener('pointercancel', this.#resumeIfTouch);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce((event) => {
    // Ignore touch/pen — only real hover (mouse) pointers should slow the marquee.
    if (event && event.pointerType && event.pointerType !== 'mouse') return;
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp(event) {
    if (event && event.pointerType && event.pointerType !== 'mouse') return;
    this.#forceSpeedUp();
  }

  #forceSpeedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  /**
   * Safety net: if a touch/pen pointer ever triggers the slow-down (e.g. a
   * stray pointerenter before pointerType could be checked), force the
   * marquee back up to full speed as soon as the touch ends or is cancelled,
   * since touch devices don't emit a natural `pointerleave`.
   * @param {PointerEvent} event
   */
  #resumeIfTouch = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#forceSpeedUp();
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          // BSG FIX: if this measurement fires while the marquee is
          // momentarily out of the visible viewport (e.g. mid-scroll, or a
          // resize event firing while scrolled away from it), rootBounds
          // can legitimately report a width of 0. Falling through with that
          // would compute numberOfCopies as 0 and collapse the marquee down
          // to nothing with the animation speed set to 0. Treat a
          // non-positive width as an invalid/transient reading and keep at
          // least 1 copy.
          const numberOfCopies =
            marqueeItemsWidth === 0 || marqueeWidth <= 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth);

          setTimeout(() => {
            resolve({
              numberOfCopies,
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    // BSG FIX: never let a bad or zero copy count produce a NaN/0 speed —
    // that gets written to --marquee-speed and, per CSS custom-property
    // substitution rules, an invalid time value falls back to 0s, which
    // permanently freezes the marquee animation.
    const safeCopies = Number.isFinite(numberOfCopies) && numberOfCopies > 0 ? numberOfCopies : 1;
    const speed = Math.sqrt(safeCopies) * speedFactor;

    return Number.isFinite(speed) && speed > 0 ? speed : speedFactor;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    // BSG FIX: this previously destructured `newNumberOfCopies`, but
    // #queryNumberOfCopies() resolves a `numberOfCopies` key — so this was
    // always `undefined`. That made #calculateSpeed(undefined) return NaN,
    // which set --marquee-speed to the invalid string "NaNs". CSS treats an
    // invalid custom-property substitution as guaranteed-invalid and falls
    // back to animation-duration's initial value (0s) — permanently
    // freezing the marquee the next time a qualifying resize fired (e.g.
    // the address-bar show/hide "resize" event some mobile browsers dispatch
    // while the page is scrolled).
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    // Guard against a transient/invalid measurement (e.g. the marquee being
    // temporarily scrolled out of view when the resize fires) ever producing
    // a non-positive copy count, which would zero out the animation speed.
    if (!newNumberOfCopies || newNumberOfCopies < 1) return;

    const currentNumberOfCopies = marqueeItems.length;

    // BSG FIX: marquee resetting to the start on scroll up. Mobile browsers
    // fire a window `resize` event when the address bar shows/hides while
    // scrolling (scrolling up brings it back, which changes the viewport
    // height and can trigger this handler even though the marquee's actual
    // *content* width never changed). We used to unconditionally rebuild
    // the DOM and call #restartAnimation() -> `currentTime = 0` on every
    // qualifying resize, which made the marquee visibly snap back to its
    // starting position on every scroll direction change. Only touch the
    // DOM / restart the animation when the required copy count has
    // genuinely changed — a same-copy-count "resize" is a false positive
    // and the marquee should keep animating uninterrupted, independent of
    // scroll direction or other page activity.
    if (newNumberOfCopies === currentNumberOfCopies) return;

    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
